import { api } from '../lib/axios';
import { Course, CourseResponse, CourseWithProgress } from '../types/course';
import { generateUuidV7 } from '@/utils/uuid';

/**
 * Get all available courses
 */
export const fetchCourses = async (): Promise<CourseResponse> => {
  try {
    const response = await api.get<any>('/api/Course');
    
    if (!response || !response.data) {
      console.warn('Course service returned null/undefined response');
      return { courses: [], totalCount: 0 };
    }
    
    // Handle different response formats
    let courses = [];
    let totalCount = 0;
    
    if (Array.isArray(response.data)) {
      // API returned an array of courses directly
      courses = response.data;
      totalCount = response.data.length;
    } else if (response.data.courses && Array.isArray(response.data.courses)) {
      // API returned { courses: [...], totalCount: n }
      courses = response.data.courses;
      totalCount = response.data.totalCount || courses.length;
    } else if (typeof response.data === 'object' && response.data !== null) {
      // Single course object
      courses = [response.data];
      totalCount = 1;
    }
    
    // Return in the expected CourseResponse format
    return { 
      courses: courses, 
      totalCount: totalCount 
    };
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

/**
 * Get public courses (limited information without auth)
 */
export const fetchPublicCourses = async (): Promise<CourseResponse> => {
  try {
    const response = await api.get<any>('/api/Course/public');
    
    if (!response || !response.data) {
      console.warn('Course service returned null/undefined response');
      return { courses: [], totalCount: 0 };
    }
    
    // Handle different response formats
    let courses = [];
    let totalCount = 0;
    
    if (Array.isArray(response.data)) {
      // API returned an array of courses directly
      courses = response.data;
      totalCount = response.data.length;
    } else if (response.data.courses && Array.isArray(response.data.courses)) {
      // API returned { courses: [...], totalCount: n }
      courses = response.data.courses;
      totalCount = response.data.totalCount || courses.length;
    }
    
    // Map response to CourseResponse format with limited information
    const formattedCourses = courses.map(course => ({
      ...course,
      description: course.description || '',
      estimatedTime: course.estimatedTime || 'No duration set',
      status: 'published' as const
    }));
    
    // Return in the expected CourseResponse format
    return { 
      courses: formattedCourses, 
      totalCount: totalCount 
    };
  } catch (error) {
    console.error('Error fetching public courses:', error);
    throw error;
  }
};

// Helper function to format estimated duration from minutes to readable string
const calculateEstimatedTime = (minutes: number): string => {
  if (minutes === 0) {
    return 'No duration set';
  } else if (minutes < 60) {
    return `${minutes} min`;
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} min`;
    }
  }
};

/**
 * Get a specific course by ID
 */
export const fetchCourseById = async (courseId: string): Promise<Course> => {
  try {
    const response = await api.get<Course>(`/api/Course/${courseId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Get course progress for the current user
 */
export const fetchCourseProgress = async (courseId: string): Promise<CourseWithProgress> => {
  try {
    const response = await api.get<CourseWithProgress>(`/api/Course/${courseId}/progress`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching progress for course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Get course certificate
 */
export const fetchCourseCertificate = async (courseId: string): Promise<any> => {
  try {
    const response = await api.get(`/api/Course/${courseId}/certificate`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching certificate for course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Create a new course (with client-side GUID generation)
 */
export const createCourse = async (courseData: Partial<Course>): Promise<Course> => {
  try {
    // Generate a new GUID for the course if not provided
    const course = {
      ...courseData,
      id: courseData.id || generateUuidV7()
    };
    
    // Ensure we're using the correct field names for the API
    if (courseData.coverImage && !courseData.imageUrl) {
      course.imageUrl = courseData.coverImage;
      delete course.coverImage;
    }
    
    const response = await api.post<Course>('/api/Course', course);
    return response.data;
  } catch (error) {
    console.error('Error creating course:', error);
    throw error;
  }
};

/**
 * Update an existing course
 */
export const updateCourse = async (courseId: string, courseData: Partial<Course>): Promise<Course> => {
  try {
    // Ensure we're using the correct field names for the API
    const updatedData = { ...courseData };
    
    if (courseData.coverImage && !courseData.imageUrl) {
      updatedData.imageUrl = courseData.coverImage;
      delete updatedData.coverImage;
    }
    
    const response = await api.put<Course>(`/api/Course/${courseId}`, updatedData);
    return response.data;
  } catch (error) {
    console.error(`Error updating course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Delete a course
 */
export const deleteCourse = async (courseId: string): Promise<void> => {
  try {
    await api.delete(`/api/Course/${courseId}`);
  } catch (error) {
    console.error(`Error deleting course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Validate if user has access to courses
 */
export const validateCourseAccess = async (role: string): Promise<boolean> => {
  try {
    const response = await api.get('/api/Health/rolevalidator', {
      params: { role }
    });
    return response.status === 200;
  } catch (error) {
    console.error('Error validating course access:', error);
    return false;
  }
}; 